import { useCallback, useEffect, useState } from 'react'
import ResultsList from '../components/ResultsList'
import Roster from '../components/Roster'
import SendToFancyNotes from '../components/SendToFancyNotes'
import SpaceActions from '../components/SpaceActions'
import SpaceHeader from '../components/SpaceHeader'
import { api } from '../lib/api'
import SpaceSettingsPage from './SpaceSettingsPage'

// One space (/admin/spaces/<id>[/results|/settings]): loads its row, draws
// the ice header — name, Send to Fancy Notes, the principal's access card,
// the People | Results | Settings tabs — and mounts the tab. People is four
// figures and the same Roster the principal uses, with the admin's extras;
// Results lists every call with a button to its write-up; Settings is the
// space form. The shell owns the URL — this page asks for a tab or the list
// through its callbacks.
export default function SpacePage({ spaceId, tab, onTab, onSpaces, onOpenResult }) {
  const [space, setSpace] = useState(null)
  const [error, setError] = useState(null)
  const [flash, setFlash] = useState(null)
  const [shareRequest, setShareRequest] = useState(0)

  // `silent` keeps the page up while the roster refreshes after an action;
  // the first load and a retry show the loading card.
  const load = useCallback((silent = false) => {
    if (!silent) {
      setSpace(null)
      setError(null)
    }
    return api(`/api/v1/spaces/${spaceId}`)
      .then(setSpace)
      .catch((err) => setError(err.detail || 'Could not load this space.'))
  }, [spaceId])

  useEffect(() => {
    load()
  }, [load])

  if (!space) {
    return (
      <main className="admin-main">
        {error ? (
          <div className="alert alert--error" role="alert">
            <div className="flex flex-col" style={{ gap: 'var(--space-xs)' }}>
              <p className="text-body-md">{error}</p>
              <div className="flex flex-wrap" style={{ gap: 'var(--space-xs)' }}>
                <button type="button" className="btn btn--secondary btn--sm" onClick={() => load()}>
                  Try again
                </button>
                <button type="button" className="btn btn--ghost btn--sm" onClick={onSpaces}>
                  All spaces
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="card" role="status">
            <p className="text-body-md" style={{ color: 'var(--color-text-secondary)' }}>
              Loading space…
            </p>
          </div>
        )}
      </main>
    )
  }

  const rows = (space.staff ?? []).filter((row) => !row.archived_at)
  const callCount = rows.reduce((sum, row) => sum + (row.calls?.length ?? 0), 0)

  const header = (
    <SpaceHeader
      space={space}
      tab={tab}
      onTab={onTab}
      onSpaces={onSpaces}
      peopleCount={rows.length}
      resultsCount={callCount}
      action={<SendToFancyNotes space={space} onChanged={setSpace} />}
      accessCard={
        <SpaceActions space={space} onChanged={setSpace} shareRequest={shareRequest} />
      }
    />
  )

  if (tab === 'settings') {
    return (
      <main>
        {header}
        <div className="admin-main">
          {/* Keyed on the space so a draft never carries over to another one. */}
          <SpaceSettingsPage
            key={space.id}
            space={space}
            onChanged={setSpace}
            onRequestShare={() => setShareRequest((count) => count + 1)}
          />
        </div>
      </main>
    )
  }

  if (tab === 'results') {
    return (
      <main>
        {header}
        <div className="admin-main">
          <ResultsList space={space} onOpenResult={onOpenResult} />
        </div>
      </main>
    )
  }

  const figures = [
    { value: rows.length, label: rows.length === 1 ? 'person added' : 'people added' },
    { value: rows.filter((row) => row.sent_at).length, label: 'invitations sent' },
    {
      value: rows.filter((row) => row.opened_at || ['opened', 'completed'].includes(row.status))
        .length,
      label: 'opened their link',
    },
    {
      value: rows.filter((row) => row.status === 'completed').length,
      label: 'conversations done',
    },
  ]

  return (
    <main>
      {header}
      <div className="admin-main">
        {flash && (
          <div
            className={`alert alert--${flash.type} text-body-sm`}
            role={flash.type === 'error' ? 'alert' : 'status'}
          >
            {flash.text}
          </div>
        )}
        <ul className="disc-stats" aria-label="Progress">
          {figures.map((figure) => (
            <li key={figure.label} className="disc-stat">
              <p className="disc-stat__num">{figure.value}</p>
              <p className="disc-stat__label">{figure.label}</p>
            </li>
          ))}
        </ul>
        <Roster
          admin
          space={space}
          onChanged={() => load(true)}
          onFlash={setFlash}
          onOpenResult={onOpenResult}
        />
      </div>
    </main>
  )
}
